import z from "zod";
import { CreatePrintJobResponse, PrintJobStatusUpdate, ShippingAddress, ShippingLevel } from "../lulu/LuluPrintTypes";
import { StripUnderscore, UnderscoreKeys } from "../util/Types";
import { OrderBase, orderBaseSchema } from "./OrderBase";


export const orderUISchema = orderBaseSchema.extend({
  _orderId: z.string().nonempty(),
  _orderShortRef: z.string().nonempty(),
  _currency: z.string().nonempty(),
  _totalAmount: z.number(),
  _totalTax: z.number(),
  _totalDiscount: z.number().optional(),
  _discountCodes: z.array(z.string()),
  _totalShipping: z.number().optional(),
  _shippingAddress : z.any(),
  _shippingLevel : z.any(),
  _luluPrintResult : z.any().optional(),
  _luluPrintUpdates : z.array(z.any())
});

export type OrderUIFields = StripUnderscore<z.infer<typeof orderUISchema>>;
export class OrderUI<TFields extends OrderUIFields = OrderUIFields> extends OrderBase<TFields> {
	_shippingAddress!: ShippingAddress;
	_shippingLevel!: ShippingLevel;
	_luluPrintResult?: CreatePrintJobResponse | undefined;
	_luluPrintUpdates!: PrintJobStatusUpdate[];
	_totalDiscount! : number | undefined;
	_discountCodes! : string[];
	_totalShipping! : number | undefined;
	override _className: string = "Order";
	override _deserializeClassName: string = "OrderUI";

	override getFieldList(): (keyof UnderscoreKeys<TFields>)[] {
		return [...super.getFieldList(), "_shippingAddress", "_shippingLevel", "_luluPrintResult", "_luluPrintUpdates", "_totalDiscount", "_discountCodes", "_totalShipping"];
	}
	constructor(props: Partial<TFields>) {
		super(props);
	}
	override validateSchema(): void {
		orderUISchema.parse(this);
	}
	get shippingAddress(): ShippingAddress {
		return this.checkFieldType<"shippingAddress">(this._shippingAddress);
	}
	set shippingAddress(value: ShippingAddress) {
		this._shippingAddress = this.checkFieldTypeAndMark<"shippingAddress">("_shippingAddress", value);
	}

	get shippingLevel(): ShippingLevel {
		return this.checkFieldType<"shippingLevel">(this._shippingLevel);
	}
	set shippingLevel(value: ShippingLevel) {
		this._shippingLevel = this.checkFieldTypeAndMark<"shippingLevel">("_shippingLevel", value);
	}

	get luluPrintResult(): CreatePrintJobResponse | undefined {
		return this.checkFieldType<"luluPrintResult">(this._luluPrintResult);
	}
	set luluPrintResult(value: CreatePrintJobResponse | undefined) {
		this._luluPrintResult = this.checkFieldTypeAndMark<"luluPrintResult">("_luluPrintResult", value);
	}
	get luluPrintUpdates(): PrintJobStatusUpdate[] {
		return this.checkFieldType<"luluPrintUpdates">(this._luluPrintUpdates);
	}
	set luluPrintUpdates(value: PrintJobStatusUpdate[]) {
		this._luluPrintUpdates = this.checkFieldTypeAndMark<"luluPrintUpdates">("_luluPrintUpdates", value);
	}
	get totalDiscount(): number | undefined {
		return this.checkFieldType<"totalDiscount">(this._totalDiscount);
	}
	set totalDiscount(value: number | undefined ) {
		this._totalDiscount = this.checkFieldTypeAndMark<"totalDiscount">("_totalDiscount", value);
	}
	get totalShipping(): number | undefined  {
		return this.checkFieldType<"totalShipping">(this._totalShipping);
	}
	set totalShipping(value: number | undefined ) {
		this._totalShipping = this.checkFieldTypeAndMark<"totalShipping">("_totalShipping", value);
	}

	get discountCodes(): string[] {
		return this.checkFieldType<"discountCodes">(this._discountCodes);
	}
	set discountCodes(value: string[]) {
		this._discountCodes = this.checkFieldTypeAndMark<"discountCodes">("_discountCodes", value);
	}
	addDiscountCode(discountCode : string){
		if(this._discountCodes.find(val => val === discountCode)) return;
		this._discountCodes.push(discountCode);
		this.checkFieldTypeAndMark<"discountCodes">("_discountCodes", this._discountCodes);
	}
	removeDiscountCode(discountCode : string){
		if(!this._discountCodes.find(val => val === discountCode)) return;
	}
	public addPrintUpdate(value: PrintJobStatusUpdate) {
		this._luluPrintUpdates.push(value);
		this.markFieldDirty("_luluPrintUpdates");
	}
	public getLatestPrintUpdate() {
        if(!this.luluPrintUpdates) return undefined;
		return this.luluPrintUpdates[this.luluPrintUpdates.length - 1];
	}
	public getLatestPrintShippingDates(): string {
        const latestUpdate = this.getLatestPrintUpdate();
        if(!latestUpdate) return "TBC";
		const dates = latestUpdate.estimated_shipping_dates;
		if (dates && dates.arrival_min && dates.arrival_max) return dates?.arrival_min + " - " + dates?.arrival_max;
		return "TBC";
	}
}
